/**
 * ProviderInstances - Página de Gestão de Instâncias de Providers
 * 
 * Painel admin para gerenciar instâncias de providers como Z-API, WAHA
 * que requerem configuração manual de token e instance_id.
 * 
 * Funcionalidades:
 * - Listagem de todas as instâncias AGRUPADAS por tenant
 * - Expand/collapse por tenant via Accordion
 * - Filtros por status, provider, tenant
 * - Cards de KPI com estatísticas
 * - Edição/liberação de instâncias
 * - Verificação de status real diretamente do provider (v1.1.0)
 * 
 * @version 1.1.0 - Adicionada verificação de status real do provider
 */

import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { 
  Search, 
  Filter, 
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  Inbox,
  RefreshCw,
  MoreVertical,
  Edit,
  Unlock,
  Ban,
  Building2,
  Copy,
  ExternalLink,
  History,
  ChevronsUpDown,
  ChevronDown,
  Server,
  ArrowRightLeft,
} from "lucide-react";
import { showToast } from "@/lib/toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { useProviderInstancesAdmin, ProviderInstance } from "@/hooks/useProviderInstances";
import { EditProviderInstanceDialog } from "@/components/admin/EditProviderInstanceDialog";
import { CreateProviderInstanceDialog } from "@/components/admin/CreateProviderInstanceDialog";
import { InstanceAuditLogDrawer } from "@/components/admin/InstanceAuditLogDrawer";
import { VerifyInstanceStatusDialog } from "@/components/admin/VerifyInstanceStatusDialog";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ConnectionStatusBadge } from "@/components/tenant/inbox/ConnectionStatusBadge";

// Interface para agrupamento por tenant
interface TenantGroup {
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
  instances: ProviderInstance[];
  stats: {
    total: number;
    pending: number;
    released: number;
    inUse: number;
    cancelled: number;
  };
}

// Configuração de badges de status
const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Clock }> = {
  pending: { label: "Pendente", variant: "secondary", icon: Clock },
  released: { label: "Liberada", variant: "default", icon: CheckCircle2 },
  in_use: { label: "Em Uso", variant: "outline", icon: Inbox },
  cancelled: { label: "Cancelada", variant: "destructive", icon: XCircle },
};

// Configuração de badges de pagamento
const paymentConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "Pendente", variant: "secondary" },
  paid: { label: "Pago", variant: "default" },
  failed: { label: "Falhou", variant: "destructive" },
  refunded: { label: "Reembolsado", variant: "outline" },
  free_trial: { label: "Free 7 dias", variant: "secondary" },
  courtesy: { label: "Cortesia", variant: "outline" },
};

export default function ProviderInstances() {
  // Estados de filtro
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [paymentFilter, setPaymentFilter] = useState<string>("all");
  const [providerFilter, setProviderFilter] = useState<string>("all");
  
  // Estados de dialogs
  const [editingInstance, setEditingInstance] = useState<ProviderInstance | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [auditLogInstance, setAuditLogInstance] = useState<ProviderInstance | null>(null);
  
  // Estado para dialog de verificação de status real (v1.1.0)
  const [verifyStatusInstance, setVerifyStatusInstance] = useState<ProviderInstance | null>(null);
  
  // Estado para controle de loading da pré-migração manual (v1.2.0)
  const [applyingMigrationId, setApplyingMigrationId] = useState<string | null>(null);
  
  // Estado para controlar quais tenants estão expandidos
  const [expandedTenants, setExpandedTenants] = useState<string[]>([]);
  
  // Query client para invalidar queries após ações
  const queryClient = useQueryClient();

  // Hook de dados
  const { 
    instances, 
    isLoading, 
    stats, 
    refetch,
    releaseInstance,
    cancelInstance 
  } = useProviderInstancesAdmin();

  // Buscar lista de providers para filtro
  const { data: providers } = useQuery({
    queryKey: ['providers-for-filter'],
    queryFn: async () => {
      const { data } = await supabase
        .from('api_providers')
        .select('id, name, slug')
        .eq('is_active', true)
        .order('name');
      return data || [];
    }
  });

  // Buscar migrações pendentes para exibir na tabela (inclui id e metadata para pré-migração manual v1.2.0)
  const { data: pendingMigrations, refetch: refetchMigrations } = useQuery({
    queryKey: ['admin-pending-migrations'],
    queryFn: async () => {
      const { data } = await supabase
        .from('provider_migrations')
        .select('id, inbox_id, old_provider_slug, new_provider_slug, reason, metadata')
        .eq('status', 'pending');
      return data || [];
    }
  });

  /**
   * Interface para migração pendente com campos completos
   */
  interface MigrationInfo {
    id: string;                          // migration_id (necessário para RPC)
    inbox_id: string;
    old_provider_slug: string;
    new_provider_slug: string;
    reason?: string | null;
    metadata?: Record<string, any> | null;
  }

  /**
   * Retorna migração pendente para uma inbox específica
   */
  const getMigrationForInbox = (inboxId: string | undefined): MigrationInfo | null => {
    if (!inboxId || !pendingMigrations) return null;
    return pendingMigrations.find(m => m.inbox_id === inboxId) as MigrationInfo || null;
  };

  /**
   * Aplica pré-migração manualmente via RPC
   * Usado quando o processo automático falha e suporte precisa intervir
   * @version 1.2.0
   */
  const handleApplyPreMigration = async (instance: ProviderInstance, migration: MigrationInfo) => {
    try {
      setApplyingMigrationId(instance.id);
      
      console.log('[Admin] Aplicando pré-migração manual:', {
        migration_id: migration.id,
        instance_id: instance.id,
        tenant_id: instance.tenant_id,
        from: migration.old_provider_slug,
        to: migration.new_provider_slug
      });
      
      const { data, error } = await supabase.rpc('start_provider_migration_admin', {
        p_migration_id: migration.id,
        p_tenant_hint: instance.tenant_id
      });

      if (error) throw error;

      console.log('[Admin] Pré-migração aplicada com sucesso:', data);
      showToast.success(
        'Pré-migração aplicada!', 
        `Instância preparada para ${getProviderDisplayName(migration.new_provider_slug)}`
      );
      
      // Refetch dados para atualizar a UI
      refetch();
      refetchMigrations();
      queryClient.invalidateQueries({ queryKey: ['admin-pending-migrations'] });
      
    } catch (error: any) {
      console.error('[Admin] Erro ao aplicar pré-migração:', error);
      showToast.error(
        'Erro ao aplicar pré-migração',
        error.message || 'Falha ao executar o processo'
      );
    } finally {
      setApplyingMigrationId(null);
    }
  };

  /**
   * Retorna nome amigável do provider para exibição
   */
  const getProviderDisplayName = (slug: string): string => {
    const names: Record<string, string> = {
      'z-api': 'Z-API',
      'waha': 'WAHA',
      'evolution': 'Evolution',
      'chatwoot': 'Chatwoot',
    };
    return names[slug] || slug.toUpperCase();
  };

  // Filtrar instâncias
  const filteredInstances = useMemo(() => {
    if (!instances) return [];
    
    return instances.filter(instance => {
      // Filtro de busca (tenant, instance ID, ID Stripe)
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesTenant = instance.tenant?.name?.toLowerCase().includes(search) ||
                              instance.tenant?.slug?.toLowerCase().includes(search);
        const matchesInstance = instance.instance_id?.toLowerCase().includes(search) ||
                                 instance.instance_name?.toLowerCase().includes(search);
        const matchesStripe = instance.stripe_subscription_item_id?.toLowerCase().includes(search) ||
                              instance.stripe_checkout_session_id?.toLowerCase().includes(search);
        if (!matchesTenant && !matchesInstance && !matchesStripe) return false;
      }
      
      // Filtro de status
      if (statusFilter !== "all" && instance.status !== statusFilter) return false;
      
      // Filtro de pagamento
      if (paymentFilter !== "all" && instance.payment_status !== paymentFilter) return false;
      
      // Filtro de provider
      if (providerFilter !== "all" && instance.provider_id !== providerFilter) return false;
      
      return true;
    });
  }, [instances, searchTerm, statusFilter, paymentFilter, providerFilter]);

  // Agrupar instâncias por tenant
  const groupedByTenant = useMemo(() => {
    if (!filteredInstances.length) return [];
    
    const groups = new Map<string, TenantGroup>();
    
    filteredInstances.forEach(instance => {
      const tenantId = instance.tenant_id;
      const existing = groups.get(tenantId);
      
      if (existing) {
        existing.instances.push(instance);
        existing.stats.total++;
        if (instance.status === 'pending') existing.stats.pending++;
        if (instance.status === 'released') existing.stats.released++;
        if (instance.status === 'in_use') existing.stats.inUse++;
        if (instance.status === 'cancelled') existing.stats.cancelled++;
      } else {
        groups.set(tenantId, {
          tenant: {
            id: tenantId,
            name: instance.tenant?.name || 'Sem nome',
            slug: instance.tenant?.slug || '-',
          },
          instances: [instance],
          stats: {
            total: 1,
            pending: instance.status === 'pending' ? 1 : 0,
            released: instance.status === 'released' ? 1 : 0,
            inUse: instance.status === 'in_use' ? 1 : 0,
            cancelled: instance.status === 'cancelled' ? 1 : 0,
          },
        });
      }
    });
    
    // Ordenar por nome do tenant
    return Array.from(groups.values())
      .sort((a, b) => a.tenant.name.localeCompare(b.tenant.name));
  }, [filteredInstances]);

  // Handler de ações
  const handleRelease = (instance: ProviderInstance) => {
    setEditingInstance(instance);
  };

  const handleCancel = async (instance: ProviderInstance) => {
    if (confirm(`Tem certeza que deseja cancelar a instância de ${instance.tenant?.name}?`)) {
      await cancelInstance.mutateAsync(instance.id);
    }
  };

  // Handlers para expandir/recolher todos
  const handleExpandAll = () => {
    setExpandedTenants(groupedByTenant.map(g => g.tenant.id));
  };

  const handleCollapseAll = () => {
    setExpandedTenants([]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Gestão de Instâncias</h1>
          <p className="text-muted-foreground">
            Gerencie instâncias de providers que requerem configuração manual
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Instância
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <Inbox className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Instâncias cadastradas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
            <p className="text-xs text-muted-foreground">Aguardando liberação</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Liberadas</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.released}</div>
            <p className="text-xs text-muted-foreground">Disponíveis para uso</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Uso</CardTitle>
            <Inbox className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{stats.inUse}</div>
            <p className="text-xs text-muted-foreground">Vinculadas a inboxes</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por tenant, instance ID ou ID Stripe..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="pending">Pendentes</SelectItem>
                <SelectItem value="released">Liberadas</SelectItem>
                <SelectItem value="in_use">Em Uso</SelectItem>
                <SelectItem value="cancelled">Canceladas</SelectItem>
              </SelectContent>
            </Select>

            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Pagamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Pagamentos</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="paid">Pago</SelectItem>
                <SelectItem value="free_trial">Free 7 dias</SelectItem>
                <SelectItem value="courtesy">Cortesia</SelectItem>
                <SelectItem value="failed">Falhou</SelectItem>
                <SelectItem value="refunded">Reembolsado</SelectItem>
              </SelectContent>
            </Select>

            <Select value={providerFilter} onValueChange={setProviderFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Providers</SelectItem>
                {providers?.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Botões Expandir/Recolher + Contador de tenants */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {groupedByTenant.length} tenant{groupedByTenant.length !== 1 ? 's' : ''} • {filteredInstances.length} instância{filteredInstances.length !== 1 ? 's' : ''}
        </p>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleExpandAll}
            disabled={groupedByTenant.length === 0}
          >
            <ChevronsUpDown className="h-4 w-4 mr-2" />
            Expandir Todos
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCollapseAll}
            disabled={expandedTenants.length === 0}
          >
            <ChevronDown className="h-4 w-4 mr-2" />
            Recolher Todos
          </Button>
        </div>
      </div>

      {/* Lista Agrupada por Tenant */}
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : groupedByTenant.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Nenhuma instância encontrada
          </CardContent>
        </Card>
      ) : (
        <Accordion 
          type="multiple" 
          value={expandedTenants} 
          onValueChange={setExpandedTenants}
          className="space-y-3"
        >
          {groupedByTenant.map((group) => (
            <AccordionItem 
              key={group.tenant.id} 
              value={group.tenant.id}
              className="border rounded-lg bg-card shadow-sm"
            >
              <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50 rounded-t-lg [&[data-state=open]]:rounded-b-none">
                <div className="flex items-center justify-between w-full pr-2">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <div className="text-left">
                      <p className="font-semibold">{group.tenant.name}</p>
                      <p className="text-xs text-muted-foreground font-normal">{group.tenant.slug}</p>
                    </div>
                  </div>
                  
                  {/* Badges de contagem */}
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs">
                      {group.stats.total} total
                    </Badge>
                    {group.stats.pending > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {group.stats.pending} pendente{group.stats.pending > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {group.stats.released > 0 && (
                      <Badge variant="default" className="text-xs bg-green-600">
                        {group.stats.released} liberada{group.stats.released > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {group.stats.inUse > 0 && (
                      <Badge variant="outline" className="text-xs border-primary text-primary">
                        {group.stats.inUse} em uso
                      </Badge>
                    )}
                    {/* Badge para instâncias canceladas */}
                    {group.stats.cancelled > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {group.stats.cancelled} cancelada{group.stats.cancelled > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              </AccordionTrigger>
              
              <AccordionContent className="px-0 pb-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead>Provider (Apelido)</TableHead>
                      <TableHead>Provider (Nome)</TableHead>
                      <TableHead>Migração</TableHead>
                      <TableHead>Cód. Suporte</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Pagamento</TableHead>
                      <TableHead>ID Stripe</TableHead>
                      <TableHead>Inbox</TableHead>
                      <TableHead>Status Inbox</TableHead>
                      <TableHead>Criado</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.instances.map((instance) => {
                      const status = statusConfig[instance.status] ?? { label: instance.status ?? '-', variant: 'outline' as const, icon: Clock };
                      const payment = paymentConfig[instance.payment_status] ?? { label: instance.payment_status ?? '-', variant: 'outline' as const };
                      const StatusIcon = status.icon;
                      const migration = getMigrationForInbox(instance.inbox?.id);
                      
                      return (
                        <TableRow key={instance.id}>
                          {/* Coluna Provider Apelido (display_name) */}
                          <TableCell>
                            <Badge variant="outline">
                              {instance.provider?.display_name || instance.provider?.name || '-'}
                            </Badge>
                          </TableCell>
                          
                          {/* Coluna Provider Nome (slug/name real) */}
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                              {instance.provider?.slug || instance.provider?.name || '-'}
                            </code>
                          </TableCell>
                          
                          {/* Coluna Migração Pendente - com indicador de status pré-migração v1.2.0 */}
                          <TableCell>
                            {migration ? (
                              <div className="flex flex-col gap-1">
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-[10px]",
                                    migration.metadata?.pre_migration_applied_at
                                      ? "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/30"
                                      : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30"
                                  )}
                                  title={`Motivo: ${migration.reason || 'upgrade de plano'}`}
                                >
                                  <ArrowRightLeft className="h-3 w-3 mr-1" />
                                  {getProviderDisplayName(migration.old_provider_slug)} → {getProviderDisplayName(migration.new_provider_slug)}
                                </Badge>
                                {/* Indicador de status da pré-migração */}
                                <span className={cn(
                                  "text-[9px]",
                                  migration.metadata?.pre_migration_applied_at
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-amber-600 dark:text-amber-400"
                                )}>
                                  {migration.metadata?.pre_migration_applied_at 
                                    ? '✓ Pré-migração aplicada' 
                                    : '⏳ Aguardando pré-migração'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          
                          {/* Código de Suporte - 8 primeiros caracteres do UUID da instância */}
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className="font-mono text-xs px-2 py-0.5 text-muted-foreground border-muted-foreground/30">
                                {instance.id.substring(0, 8).toUpperCase()}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => {
                                  navigator.clipboard.writeText(instance.id.substring(0, 8).toUpperCase());
                                  showToast.success('Código copiado!');
                                }}
                                title="Copiar código de suporte"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <Badge variant={status.variant} className="gap-1">
                              <StatusIcon className="h-3 w-3" />
                              {status.label}
                            </Badge>
                          </TableCell>
                          
                          <TableCell>
                            <Badge variant={payment.variant}>
                              {payment.label}
                            </Badge>
                          </TableCell>
                          
                          {/* Coluna ID Stripe - para rastreamento e verificação manual */}
                          <TableCell>
                            {instance.stripe_subscription_item_id ? (
                              <div className="flex items-center gap-1">
                                <code className="text-xs bg-muted px-2 py-1 rounded max-w-[120px] truncate" title={instance.stripe_subscription_item_id}>
                                  {instance.stripe_subscription_item_id}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => {
                                    navigator.clipboard.writeText(instance.stripe_subscription_item_id!);
                                    showToast.success('ID copiado!');
                                  }}
                                  title="Copiar ID Stripe"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                                <a
                                  href={`https://dashboard.stripe.com/subscriptions?query=${instance.stripe_subscription_item_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-primary"
                                  title="Abrir no Stripe"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </div>
                            ) : instance.source_type === 'manual' ? (
                              <Badge variant="outline" className="text-xs">Manual</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          
                          {/* Coluna Inbox - nome e ID */}
                          <TableCell>
                            {instance.inbox ? (
                              <div className="flex items-center gap-2">
                                {/* Badge com ID (8 primeiros caracteres do UUID em maiúsculas) */}
                                <Badge variant="outline" className="font-mono text-[10px] px-1.5 py-0 h-5 text-muted-foreground border-muted-foreground/30">
                                  {instance.inbox.id.substring(0, 8).toUpperCase()}
                                </Badge>
                                <span className="text-sm">{instance.inbox.name}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          
                          {/* Coluna Status Inbox - conexão com provider */}
                          <TableCell>
                            {instance.inbox?.status ? (
                              <ConnectionStatusBadge 
                                status={instance.inbox.status} 
                                showLabel={true}
                              />
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDistanceToNow(new Date(instance.created_at), {
                              addSuffix: true,
                              locale: ptBR,
                            })}
                          </TableCell>
                          
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setEditingInstance(instance)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                
                                <DropdownMenuItem onClick={() => setAuditLogInstance(instance)}>
                                  <History className="h-4 w-4 mr-2" />
                                  Histórico
                                </DropdownMenuItem>
                                
                                {/* Opção de verificar status real - apenas para instâncias em uso com inbox */}
                                {instance.status === 'in_use' && instance.inbox && (
                                  <DropdownMenuItem onClick={() => setVerifyStatusInstance(instance)}>
                                    <Server className="h-4 w-4 mr-2" />
                                    Verificar Status Real
                                  </DropdownMenuItem>
                                )}
                                
                                {/* Opção de aplicar pré-migração manual - v1.2.0 */}
                                {/* Aparece quando há migração pendente e pré-migração ainda não foi aplicada */}
                                {migration && !migration.metadata?.pre_migration_applied_at && (
                                  <DropdownMenuItem 
                                    onClick={() => handleApplyPreMigration(instance, migration)}
                                    disabled={applyingMigrationId === instance.id}
                                  >
                                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                                    {applyingMigrationId === instance.id ? 'Aplicando...' : 'Aplicar Pré-Migração'}
                                  </DropdownMenuItem>
                                )}
                                
                                {instance.status === 'pending' && (
                                  <DropdownMenuItem onClick={() => handleRelease(instance)}>
                                    <Unlock className="h-4 w-4 mr-2" />
                                    Liberar
                                  </DropdownMenuItem>
                                )}
                                
                                {instance.status !== 'cancelled' && instance.status !== 'in_use' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      className="text-destructive"
                                      onClick={() => handleCancel(instance)}
                                    >
                                      <Ban className="h-4 w-4 mr-2" />
                                      Cancelar
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Dialogs */}
      <EditProviderInstanceDialog
        instance={editingInstance}
        open={!!editingInstance}
        onOpenChange={(open) => !open && setEditingInstance(null)}
      />
      
      <CreateProviderInstanceDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
      
      <InstanceAuditLogDrawer
        open={!!auditLogInstance}
        onOpenChange={(open) => !open && setAuditLogInstance(null)}
        instanceId={auditLogInstance?.id || ""}
        instanceName={auditLogInstance?.id.substring(0, 8).toUpperCase()}
      />
      
      {/* Dialog de verificação de status real (v1.1.0) */}
      <VerifyInstanceStatusDialog
        open={!!verifyStatusInstance}
        onOpenChange={(open) => !open && setVerifyStatusInstance(null)}
        instance={verifyStatusInstance}
      />
    </div>
  );
}
